import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {IHvPaket} from '../../../../models/hvpaket';
import {DataService} from '../../../../service/data.service';
import {PopupService} from '../../../../service/popup.service';
import {PartnerComponent} from '../../../partner/partner.component';
import {toggleEditMode} from '../../../../../shared/utils';
import {merge, Observable} from 'rxjs';
import {HeimvorteilProduktart} from '../../../../../shared/enum';
import {LookupService} from '../../../../service/lookup.service';

@Component({
  selector: 'app-hv-paket',
  templateUrl: './hv-paket.component.html',
  styleUrls: ['./hv-paket.component.scss']
})
export class HvPaketComponent implements OnInit {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  produktart: HeimvorteilProduktart;

  @Input()
  paket: IHvPaket;

  @Input()
  previewPaket: IHvPaket;

  @Output()
  calculateRatePendingEvent = new EventEmitter<void>();

  @ViewChild(PartnerComponent) partnerComponent: PartnerComponent;

  constructor(public translateService: TranslateService, public popupService: PopupService, public dataService: DataService, private ref: ChangeDetectorRef,
              private lookupService: LookupService) { }

  ngOnInit() {
    this.onValueChanged();
    this.toggleEditMode();
  }

  static createFormGroup(fb: UntypedFormBuilder, expertMode: boolean) {
    return fb.group({
      selected: new UntypedFormControl(false),
      versicherungssumme: new UntypedFormControl('', Validators.required),
      geltungsbereicheVersicherungssummen: new UntypedFormControl('', Validators.required),
      partner: PartnerComponent.createFormGroup(fb, expertMode)
    });
  }

  get selected() { return this.formGroup.get('selected'); }
  get versicherungssumme() { return this.formGroup.get('versicherungssumme'); }
  get geltungsbereicheVersicherungssummen() { return this.formGroup.get('geltungsbereicheVersicherungssummen'); }
  get partner() { return this.formGroup.get('partner'); }

  get showVersicherungssumme() {
    return this.selected.value && this.paket.Versicherungssummen ? true : false;
  }

  get showGeltungsbereicheVersicherungssummen() {
    return this.selected.value && this.paket.GeltungsbereicheVersicherungssummen ? true : false;
  }

  get showPartner() {
    return this.selected.value && this.paket.HasLocationIndustrieRisiko ? true : false;
  }

  toggleEditMode(): void {
    toggleEditMode(this.versicherungssumme, this.showVersicherungssumme);
    toggleEditMode(this.geltungsbereicheVersicherungssummen, this.showGeltungsbereicheVersicherungssummen);
  }

  onValueChanged(): void {
    merge(
      this.selected.valueChanges,
      this.versicherungssumme.valueChanges,
      this.geltungsbereicheVersicherungssummen.valueChanges
    ).subscribe(() => {
      this.calculateRatePendingEvent.emit();
    });
  }

  selectPaket(): void {
    this.selected.setValue(!this.selected.value);
    this.toggleEditMode();
    this.ref.detectChanges();
  }

  getPraemie(): number {
    const geltungsbereich = (this.versicherungssumme.value ?? this.geltungsbereicheVersicherungssummen.value);
    return this.paket.Praemie ? this.paket.Praemie :
      geltungsbereich ? this.paket.Geltungsbereiche.find(x => x.Id === Number(geltungsbereich.Id))?.Praemie : null;
  }

  getZusatzpaket(): IHvPaket {
    if (this.selected.value) {
      return {
        Id: this.paket.Id,
        VersicherungssummeId: this.versicherungssumme.value ? Number(this.versicherungssumme.value.Id) : null,
        GeltungsbereicheVersicherungssummeId: this.geltungsbereicheVersicherungssummen.value ? Number(this.geltungsbereicheVersicherungssummen.value.Id) : null,
        Titel: this.paket.Titel,
        Versicherungssummen: this.paket.Versicherungssummen,
        GeltungsbereicheVersicherungssummen: this.paket.GeltungsbereicheVersicherungssummen,
        Partner: this.paket.HasLocationIndustrieRisiko ? this.partnerComponent.getPartner() : null,
        HasLocationIndustrieRisiko: this.paket.HasLocationIndustrieRisiko
      } as IHvPaket;
    }
    return null;
  }

  setZusatzpaket(paket: IHvPaket): void {

    this.selected.setValue(true);
    if(!!paket.VersicherungssummeId) {
      this.versicherungssumme.setValue(this.paket.Versicherungssummen.find(x =>
        x.Id === paket.VersicherungssummeId.toString()
      ));
      this.paket.VersicherungssummeId = paket.VersicherungssummeId;
    }
    if(!!paket.Partner) {
      this.paket.HasLocationIndustrieRisiko = true;
      this.partnerComponent.setPartner(paket.Partner);
      this.paket.Partner = paket.Partner;
    }
    if(!!paket.GeltungsbereicheVersicherungssummeId){
      this.geltungsbereicheVersicherungssummen.setValue(this.paket.GeltungsbereicheVersicherungssummen.find(
        x => x.Id === paket.GeltungsbereicheVersicherungssummeId.toString()
      ));
      this.paket.GeltungsbereicheVersicherungssummeId = paket.GeltungsbereicheVersicherungssummeId;
    }



    this.toggleEditMode();
    this.ref.detectChanges();
  }

  getHvPaketFactsheets(produktart: HeimvorteilProduktart, kategorie: number): Observable<string>{

    let key = 'NOKEY';  // fallback
    if(produktart === HeimvorteilProduktart.Eigenheim){
          key = this.dataService.hvPaketFactsheetEhUrls.get(kategorie);
    }
    if(produktart === HeimvorteilProduktart.Haushalt){
      key = this.dataService.hvPaketFactsheetHhUrls.get(kategorie);
    }

    return this.translateService.get(key);
  }
}
